/**
 * AppBackService — deterministischer App-Back via TanStack location state.
 *
 * KEIN globaler sessionStorage-Stack, KEIN AppHistoryService, KEIN native
 * history.back(). Stattdessen setzt JEDER relevante Link / navigate-Aufruf
 * `state.from` als validierte Breadcrumb-Chain der committed App-Locations.
 *
 * Die Chain ist ein serialisierbarer, rekursiver FromChain-Typ:
 *   { href: string; from?: FromChain }
 *
 * Beim AppLink wird die aktuelle Location inklusive ihrer validierten Chain
 * als predecessor eingebettet. Beim Back navigiert replace:true zum predecessor
 * href UND setzt state.from auf dessen eigenen predecessor, damit wiederholtes
 * Back funktioniert. Bounded depth (32), cycle/foreign URL validation.
 *
 * Browser Back/Forward funktioniert weiterhin TanStack-nativ;
 * der App-Back folgt dem expliziten fachlichen `from`.
 */

import type { LinkOptions, ParsedLocation } from '@tanstack/react-router';
import { AppRoute } from '@agentselly/immodossier-shared';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_CHAIN_DEPTH = 32;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AppBackOptions {
  /** Fallback-Route wenn kein from-State existiert (Deep-Link / Reload). */
  readonly fallbackTo?: AppRoute | string;
  readonly fallbackParams?: Record<string, string>;
  readonly fallbackHash?: string;
}

/**
 * Serialisierbare Breadcrumb-Chain der committed App-Locations.
 * Jeder Knoten enthält den href der Location und optional den predecessor.
 * Depth-bounded auf MAX_CHAIN_DEPTH.
 */
export interface FromChain {
  readonly href: string;
  readonly from?: FromChain;
}

/**
 * Typisierter from-State, der in TanStack location state gemerged wird.
 * TanStack Router v1 merged `state` in `ParsedLocation.state` als
 * `Record<string, unknown>`.
 */
export interface FromState {
  /** Breadcrumb-Chain der committed App-Locations. */
  readonly from: FromChain;
  /**
   * Optionale Navigations-ID, um stale/cyclic from-States zu erkennen.
   * Wird vom zentralen Helper gesetzt.
   */
  readonly fromNavigationId?: string;
}

// ─── Navigation Counter ──────────────────────────────────────────────────────

let navigationCounter = 0;

// ─── Chain Validation ─────────────────────────────────────────────────────────

/**
 * Validiert rekursiv eine FromChain-Struktur.
 * Prüft: href ist nicht-leerer String, depth ≤ maxDepth, keine Zyklen.
 * currentHref wird zusätzlich geprüft, damit kein Chain-Eintrag die
 * aktuelle Location referenziert (cyclic guard für tiefe Chains).
 */
function isValidChain(
  chain: unknown,
  maxDepth: number,
  currentHref?: string,
  seen?: Set<string>,
): chain is FromChain {
  if (maxDepth <= 0) return false;
  if (!chain || typeof chain !== 'object') return false;

  const c = chain as Record<string, unknown>;
  if (typeof c.href !== 'string' || !c.href) return false;

  // Cyclic guard: href darf nicht die aktuelle Location sein
  if (c.href === currentHref) return false;

  // Cycle detection: gleicher href darf nicht zweimal in der Chain vorkommen
  if (seen?.has(c.href)) return false;
  const newSeen = seen ?? new Set<string>();
  newSeen.add(c.href);

  if (c.from !== undefined) {
    return isValidChain(c.from, maxDepth - 1, currentHref, newSeen);
  }
  return true;
}

/**
 * Trimmt eine FromChain auf maximal maxDepth Tiefe.
 */
function trimChain(
  chain: FromChain | undefined,
  maxDepth: number,
): FromChain | undefined {
  if (!chain || maxDepth <= 0) return undefined;
  return {
    href: chain.href,
    from: trimChain(chain.from, maxDepth - 1),
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extrahiert die validierte FromChain aus einer TanStack Location.
 * Validiert: from muss eine gültige FromChain sein, href darf nicht die
 * aktuelle Location referenzieren (cyclic guard), depth ≤ MAX_CHAIN_DEPTH.
 *
 * Returns `undefined` wenn kein gültiger from-State existiert.
 */
export function getValidatedFromState(
  location: ParsedLocation,
): FromChain | undefined {
  const rawFrom = (location.state as any)?.from;
  if (!rawFrom || typeof rawFrom !== 'object') {
    return undefined;
  }

  // Cyclic guard: from.href darf nicht die aktuelle Location sein
  if (rawFrom.href === location.href) {
    return undefined;
  }

  // Vollständige Chain-Validierung
  if (!isValidChain(rawFrom, MAX_CHAIN_DEPTH, location.href)) {
    return undefined;
  }

  return rawFrom as FromChain;
}

/**
 * Prüft, ob ein gültiger from-State für den Back-Button existiert.
 */
export function hasValidFromState(location: ParsedLocation): boolean {
  return getValidatedFromState(location) !== undefined;
}

/**
 * Zentrale typed API: Nimmt Link/Navigate-Optionen und injectiert `state.from`
 * als Breadcrumb-Chain mit der aktuellen committed Location.
 *
 * Usage:
 *   navigate(withFromState(router, { to: '/somewhere' }))
 *   <Link {...withFromState(router, { to: '/somewhere', params: {...} })} />
 *
 * Die Chain enthält den vollständigen href der aktuellen Location plus deren
 * validierten predecessor, depth-bounded auf MAX_CHAIN_DEPTH.
 */
export function withFromState<TOptions extends Record<string, unknown>>(
  currentLocation: ParsedLocation,
  options: TOptions,
): TOptions & { state: FromState } {
  const navId = `nav_${++navigationCounter}_${Date.now()}`;

  // Bestehende Chain aus der aktuellen Location lesen und validieren
  const currentChain = getValidatedFromState(currentLocation);

  // Neue Chain: aktuelle Location als head, bestehende Chain als tail
  // Depth-bounded: die bestehende Chain wird auf MAX_CHAIN_DEPTH - 1 getrimmt
  const newChain: FromChain = {
    href: currentLocation.href,
    from: trimChain(currentChain, MAX_CHAIN_DEPTH - 1),
  };

  return {
    ...options,
    state: {
      from: newChain,
      fromNavigationId: navId,
    } satisfies FromState,
  } as TOptions & { state: FromState };
}

/**
 * Convenience: Erzeugt LinkOptions mit from-State für programmatic navigation.
 * Verwendet `any`-Cast, weil TanStack Router v1 generische Typ-Parameter
 * für `to`/`params` hat, die wir hier nicht auflösen können.
 */
export function navigateWithFromState(
  currentLocation: ParsedLocation,
  to: string,
  extra?: Record<string, unknown>,
): LinkOptions {
  return withFromState(currentLocation, {
    to,
    ...extra,
  } as Record<string, unknown>) as unknown as LinkOptions;
}

// ─── BackButton-Logik ────────────────────────────────────────────────────────

/**
 * Führt App-Back aus: navigiert zur `from`-Location der aktuellen TanStack
 * Location mit `replace:true`. Setzt den Ziel-State auf dessen eigenen
 * predecessor (chain.from), damit wiederholtes Back funktioniert.
 *
 * Wenn kein gültiger from-State existiert, wird fallbackTo verwendet.
 */
export function executeAppBack(
  currentLocation: ParsedLocation,
  navigate: (options: LinkOptions) => void,
  options?: AppBackOptions,
): void {
  const chain = getValidatedFromState(currentLocation);

  if (chain) {
    // Navigiere zur from-Location mit replace:true.
    // Setze state.from auf den predecessor des Ziels (chain.from),
    // damit wiederholtes Back funktioniert.
    const url = new URL(chain.href, window.location.origin);
    const navigateOptions: Record<string, unknown> = {
      to: url.pathname,
      search: url.search
        ? Object.fromEntries(url.searchParams.entries())
        : undefined,
      hash: url.hash ? url.hash.replace(/^#/, '') : undefined,
      replace: true,
    };

    // Rest der Chain als state.from setzen, damit Back#N+1 funktioniert
    if (chain.from) {
      navigateOptions.state = { from: chain.from };
    }

    navigate(navigateOptions as any);
    return;
  }

  // Kein gültiger from-State — fallback
  if (options?.fallbackTo) {
    navigate({
      to: options.fallbackTo as any,
      params: options.fallbackParams as any,
      hash: options.fallbackHash,
      replace: true,
    } as any);
    return;
  }

  // Fallback: App-Root
  navigate({ to: '/' as any, replace: true } as any);
}
